import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Input } from '@angular/core';

@Component({
    selector: 'app-video-recorder',
    templateUrl: './video-recorder.component.html',
    styleUrls: ['./video-recorder.component.scss'],
    standalone: true,
    imports: [
        CommonModule
    ]
})

export class VideoRecorderComponent implements OnInit, OnDestroy {

    @ViewChild('videoPreview') videoPreview!: ElementRef<HTMLVideoElement>;
    @Output() recordingStarted = new EventEmitter<void>();
    @Output() recordingStopped = new EventEmitter<void>();
    @Input() maxRecordingTime: number = 30;
    @Output() recordingComplete = new EventEmitter<{ blob: Blob, url: string }>();


    readonly MAX_RECORDING_TIME = 30;

    facingMode: 'user' | 'environment' = 'user';
    stream: MediaStream | null = null;
    recorder: MediaRecorder | null = null;
    chunks: Blob[] = [];
    isRecording: boolean = false;
    recordingTime: number = 0;
    remainingTime: number = this.MAX_RECORDING_TIME;
    timerInterval: any;
    recordedVideoUrl: string | null = null;
    cameraReady: boolean = false;
    private blobUrl: string | null = null;
    private recordedBlob: Blob | null = null;

    constructor() { }

    ngOnInit(): void { }



    startRecording(): void {
        if (!this.stream) return;

        this.chunks = [];
        this.recorder = new MediaRecorder(this.stream);

        this.recorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
                this.chunks.push(e.data);
            }
        };

        this.recorder.onstop = () => {
            const blob = new Blob(this.chunks, { type: 'video/webm' });
            const url = URL.createObjectURL(blob);
            this.recordedVideoUrl = url;
            this.blobUrl = url;

            // Emit the object with both blob and url
            this.recordingComplete.emit({ blob, url });
        };

        this.recorder.start();
        this.isRecording = true;
        this.recordingTime = 0;
        this.remainingTime = this.maxRecordingTime; // Use the input property
        this.recordingStarted.emit();

        this.timerInterval = setInterval(() => {
            this.recordingTime++;
            this.remainingTime = Math.max(0, this.maxRecordingTime - this.recordingTime);

            // Auto-stop when reaching max recording time
            if (this.remainingTime <= 0) {
                this.stopRecording();
            }
        }, 1000);
    }

    getRecordedBlob(): Blob | null {
        return this.recordedBlob;
    }

    stopRecording(): void {
        if (this.recorder && this.isRecording) {
            this.recorder.stop();
            this.isRecording = false;
            clearInterval(this.timerInterval);
            this.recordingStopped.emit();
        }
    }

    toggleRecording(): void {
        if (this.isRecording) {
            this.stopRecording();
        } else {
            this.startRecording();
        }
    }

    formatRemainingTime(): string {
        const minutes = Math.floor(this.remainingTime / 60);
        const seconds = this.remainingTime % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    async switchCamera() {
        // Don't allow switching while recording
        if (this.isRecording) return;

        // Stop current stream tracks
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        // Toggle facing mode
        this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';

        // Start camera with new facing mode
        await this.startCameraWithFacingMode();
    }

    async startCamera(): Promise<void> {
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
            });
            if (this.videoPreview && this.videoPreview.nativeElement) {
                this.videoPreview.nativeElement.srcObject = this.stream;
                this.cameraReady = true;
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
        }
    }

    async startCameraWithFacingMode(): Promise<void> {
        try {
            // Stop any existing stream
            if (this.stream) {
                this.stream.getTracks().forEach(track => track.stop());
            }

            // Get new stream with current facing mode
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: this.facingMode },
                audio: true
            });

            // Update video element
            if (this.videoPreview && this.videoPreview.nativeElement) {
                this.videoPreview.nativeElement.srcObject = this.stream;
            }
        } catch (err) {
            console.error('Error switching camera:', err);
            // If we fail to get the new facing mode, try to fall back to any camera
            try {
                this.stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: true
                });
                if (this.videoPreview && this.videoPreview.nativeElement) {
                    this.videoPreview.nativeElement.srcObject = this.stream;
                }
            } catch (fallbackErr) {
                console.error('Error falling back to default camera:', fallbackErr);
            }
        }
    }

    cleanup(): void {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        if (this.recorder) {
            if (this.isRecording) {
                this.recorder.stop();
            }
            this.recorder = null;
        }

        clearInterval(this.timerInterval);
        this.cameraReady = false;
        this.recordedVideoUrl = null;
        this.remainingTime = this.maxRecordingTime;
    }

    resetRecording(): void {
        this.recordedVideoUrl = null;
        this.recordingTime = 0;
        this.remainingTime = this.maxRecordingTime;
    }

    ngOnDestroy(): void {
        this.cleanup();
        if (this.blobUrl) {
            URL.revokeObjectURL(this.blobUrl);
        }
    }
}